// breakpoints.js — THE single source of truth for responsive pixel cuts.
// ISP-4018 · Wave-6 responsive retrofit U0a (00-RESPONSIVE-SYSTEM-and-REUSE-MAP §1).
//
// WHY THIS FILE EXISTS
// The app measures screen size in ~54 ad-hoc places (`useScreenSize`, raw
// `innerWidth`, scattered `@media`), and those cuts DISAGREE with each other
// (useScreenSize calls 768 "mobile"; the retrofit system calls 768 tablet-
// portrait). This module fixes ONE band system aligned to MUI v5's defaults
// (sm:600 · md:900 · lg:1200) so styled-components media queries, MUI's
// `theme.breakpoints`, and JS branches can never drift apart.
//
// ADDITIVE + INERT: introducing this file changes NOTHING until a component
// imports it. `useScreenSize.js` keeps working (now @deprecated) during the
// opportunistic migration. Do NOT sed-replace call sites — the semantics differ
// per site (see the migration table in useScreenSize.js / system doc §1.4).
//
// THREE IDIOMS, ONE NUMBER SET:
//   • styled-components / CSS  → `mq.phone`, `mq.tabletP`, …
//   • MUI components           → `theme.breakpoints.down('sm')` (values wired in
//                                 utils/theme.js from BP_VALUES below)
//   • JS branches              → `useBreakpoint()` → { bp, isPhone, … }

import { useEffect, useState } from "react";

// ── Numeric cuts (px). NEVER hardcode these numbers anywhere else. ────────────
// Bands (ranges, not test points):
//   phone     < 600            (375, 430)
//   tabletP   600 – 899        (768)
//   tabletL   900 – 1199       (1024)
//   desktop   >= 1200          (1280, 1440)
export const BP = { phoneMax: 599, tabletPMax: 899, tabletLMax: 1199 };

// MUI breakpoint values — declared identical to MUI v5 defaults so wiring them
// into createTheme is a no-op behavior change that makes the agreement explicit
// and lint-able. (xs:0 · sm:600 · md:900 · lg:1200 · xl:1536)
export const BP_VALUES = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };

// ── Named builder cut (Gordon decision 2026-07-12: full builder is responsive
// DOWN TO tablet-portrait; phone < 600 gets Quick-Edit or a LargerScreenGate).
// The builder's hard floor width — below this the full craft.js editor is gated,
// not crammed. Exported as a named cut so the builder unit reads ONE number.
export const BUILDER_FLOOR = 768;

// ── Named builder cuts (REC-1, Gordon 2026-07-12). The full builder keeps its
// OWN surface-specific breakpoints (Gordon's 768 floor is verbatim) as named
// exports here so the R-2 "no hardcoded widths outside this file" law still
// holds. The builder unit (U2) reads ONLY these three numbers:
//   >= 1200            desktop  — current 3-column layout, PIXEL-LOCKED (untouched)
//   1024 – 1199        tabletL  — in-grid fixed-px panel, SegmentView stacks below
//   768 – 1023         drawer   — tool panel becomes the scrimless side PanelDrawer
//   <= 767             phone    — Quick-Edit surface (separate unit U3)
export const BUILDER_PHONE_MAX = 767; // <= this ⇒ phone (Quick-Edit, U3)
export const BUILDER_DRAWER_MAX = 1023; // <= this (and > phone) ⇒ drawer band

// Pure builder-layout-mode resolver (unit-testable; no window dependency).
// U2 owns 768–1199; >=1200 stays desktop/legacy pixel-locked.
//
// QA-1279 (Gordon 2026-08-25): the "drawer" overlay mode (768–1023) is RETIRED
// for tablet. MEASURED on this build (/builder/4756 @834, Wheel panel open): the
// responsive PanelDrawer rendered `position:fixed` OFF the visible canvas while a
// second panel covered the wheel's right half (its center/portrait hidden), and
// the drawer's `padding-right` reservation never reached LeftSection — the whole
// overlay path was broken and unshipped. The IN-GRID "tabletL" panel (a flex
// sibling that shrinks the canvas so the wheel re-fits and stays centered in the
// remaining space) MEASURED clean at 1024/1194 (nothing hidden), so it now owns
// the entire tablet range. Below 768 is GATED to the app-download placeholder
// (shouldGateBuilder), so the returned mode there is moot — kept as "tabletL"
// (never "drawer") for any non-gated caller.
export function builderLayoutMode(width) {
  const w = Number(width) || 0;
  if (w >= BP.tabletLMax + 1) return "desktop"; // >= 1200
  return "tabletL"; // 768 – 1199 (in-grid panel; drawer overlay retired)
}

// Short-viewport axis (the Wheel stage + pinned CTAs respond to height too;
// tablet-landscape 1024×768 IS a short viewport).
export const SHORT_VIEWPORT_MAX_HEIGHT = 700;

// ── Media-query strings for styled-components ────────────────────────────────
export const mq = {
  phone: `@media (max-width: ${BP.phoneMax}px)`, // phone only (<600)
  tabletP: `@media (max-width: ${BP.tabletPMax}px)`, // tabletP AND phone (<=899)
  tabletPOnly: `@media (min-width: ${BP.phoneMax + 1}px) and (max-width: ${BP.tabletPMax}px)`,
  tabletL: `@media (max-width: ${BP.tabletLMax}px)`, // everything below desktop (<=1199)
  desktop: `@media (min-width: ${BP.tabletLMax + 1}px)`, // >=1200
  touch: `@media (hover: none), (pointer: coarse)`,
  hover: `@media (hover: hover) and (pointer: fine)`,
  shortViewport: `@media (max-height: ${SHORT_VIEWPORT_MAX_HEIGHT}px)`,
};

// Raw query bodies (no `@media` prefix) for matchMedia() consumers.
const QUERY = {
  phone: `(max-width: ${BP.phoneMax}px)`,
  tabletP: `(min-width: ${BP.phoneMax + 1}px) and (max-width: ${BP.tabletPMax}px)`,
  tabletL: `(min-width: ${BP.tabletPMax + 1}px) and (max-width: ${BP.tabletLMax}px)`,
  desktop: `(min-width: ${BP.tabletLMax + 1}px)`,
  touch: `(hover: none), (pointer: coarse)`,
  short: `(max-height: ${SHORT_VIEWPORT_MAX_HEIGHT}px)`,
};

// ── Pure band resolver (unit-testable; no window dependency) ──────────────────
// Given a width in px, return the band token. Ranges are inclusive of the min.
export function bandForWidth(width) {
  const w = Number(width) || 0;
  if (w <= BP.phoneMax) return "phone";
  if (w <= BP.tabletPMax) return "tabletP";
  if (w <= BP.tabletLMax) return "tabletL";
  return "desktop";
}

const safeMatch = (query) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
};

function readState() {
  const width = typeof window === "undefined" ? 1280 : window.innerWidth;
  const height = typeof window === "undefined" ? 800 : window.innerHeight;
  const bp = bandForWidth(width);
  return {
    bp,
    width,
    height,
    isPhone: bp === "phone",
    isTabletP: bp === "tabletP",
    isTabletL: bp === "tabletL",
    isDesktop: bp === "desktop",
    // convenience unions the retrofit uses constantly:
    isTouchBand: bp === "phone" || bp === "tabletP", // layout-touch bands (<900)
    isTouch: safeMatch(QUERY.touch), // actual coarse-pointer device
    isShort: safeMatch(QUERY.short),
  };
}

// ── The hook (matchMedia-based; one shared resize listener) ───────────────────
// Returns { bp:'phone'|'tabletP'|'tabletL'|'desktop', width, isPhone, isTabletP,
//           isTabletL, isDesktop, isTouchBand, isTouch, isShort }.
export function useBreakpoint() {
  const [state, setState] = useState(readState);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let frame = null;
    const update = () => {
      // Coalesce bursts of resize events into one state read per frame.
      if (frame != null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setState((prev) => {
          const next = readState();
          // Only re-render when a meaningful signal actually changed.
          if (
            prev.bp === next.bp &&
            prev.isTouch === next.isTouch &&
            prev.isShort === next.isShort
          ) {
            return prev;
          }
          return next;
        });
      });
    };

    // Sync once on mount (covers a resize that happened before hydration).
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return state;
}

const breakpoints = {
  BP,
  BP_VALUES,
  BUILDER_FLOOR,
  BUILDER_PHONE_MAX,
  BUILDER_DRAWER_MAX,
  builderLayoutMode,
  mq,
  bandForWidth,
  useBreakpoint,
};
export default breakpoints;
